import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Users, Zap, Radar, Coins, ExternalLink } from "lucide-react";
import { BubbleMap } from "./BubbleMap";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtToken = (n) => {
  if (!Number.isFinite(n) || n <= 0) return "0";
  return Math.round(n).toLocaleString();
};
const fmtDropAmount = (n) => {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return Math.round(n).toString();
};
const fmtAgo = (unixSec) => {
  if (!unixSec) return "";
  const diff = Math.max(0, Math.floor(Date.now() / 1000 - unixSec));
  if (diff < 15) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};
const shortWallet = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : "");
const fmtUsd = (n) => {
  if (!Number.isFinite(n) || n <= 0) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

export const AirdropSummary = () => {
  const [state, setState] = useState(null);
  const [transfers, setTransfers] = useState({ community: [], excluded: [] });
  // Ticks up ONLY when the backend polled txCount actually increments
  // from a previously-observed value (never on first load).
  const [newDropTick, setNewDropTick] = useState(0);
  const lastTxCountRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          axios.get(`${API}/api/airdrop`),
          axios.get(`${API}/api/airdrop/transfers`),
        ]);
        if (!alive) return;
        setState(s.data);
        setTransfers({
          community: t.data.community || [],
          excluded: t.data.excluded || [],
        });
        // Snapshot the baseline txCount so the "NEW" badge never fires while
        // polling is paused. If polling is turned back on later, first tick
        // establishes a fresh baseline.
        lastTxCountRef.current = Number(s.data?.txCount ?? 0);
      } catch {
        /* keep last */
      }
    })();
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => {
    const dropsSent = transfers.community.length;
    const walletsFed = state?.walletsFed ?? 0;
    return {
      dropsSent,
      walletsFed,
      total: state?.totalAirdropped ?? 0,
      currentValue: state?.currentValue ?? 0,
      valueAtAth: state?.valueAtAth ?? 0,
      lastUpdated: state?.lastUpdated,
    };
  }, [state, transfers]);

  const lastUpdatedLabel = useMemo(() => {
    if (!stats.lastUpdated) return "syncing…";
    try {
      return new Date(stats.lastUpdated).toLocaleString();
    } catch {
      return "syncing…";
    }
  }, [stats.lastUpdated]);

  // Most-recent drop (backend already returns community sorted desc by blockTime).
  const latestDrop = transfers.community[0] || null;
  // Re-render the "X min ago" every 30s without hitting the API.
  // Polling is paused, so we don't advance the "X min ago" label either — it
  // stays at whatever value was computed from the initial fetch.
  const [nowTick] = useState(0);
  const latestAgoLabel = useMemo(
    () => (latestDrop ? fmtAgo(latestDrop.blockTime) : ""),
    [latestDrop, nowTick]
  );

  return (
    <section
      id="airdrop"
      data-testid="airdrop-summary-section"
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="mb-8 sm:mb-10 flex items-end justify-between flex-wrap gap-4"
      >
        <div>
          <p className="font-bebas tracking-[0.35em] text-[#9945FF] text-lg flex items-center gap-2">
            <Radar size={16} className="text-[#14F195]" /> LIVE TRACKER
          </p>
          <h2
            data-testid="airdrop-heading"
            className="font-marker text-4xl sm:text-5xl gradient-sol text-glow-green mt-2 -rotate-1 inline-block"
          >
            AIRDROPS
          </h2>
        </div>
        <p className="font-marker text-zinc-500 rotate-1 text-sm sm:text-base">receipts. broadcast to the herd.</p>
      </motion.div>

      {/* Latest drop ticker — pill above the stat grid, updates whenever the polled data reveals a newer drop */}
      {latestDrop && (
        <a
          href={`https://solscan.io/tx/${latestDrop.signature}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="airdrop-latest-ticker"
          key={latestDrop.signature}
          className="latest-drop-ticker mb-4 sm:mb-6 inline-flex items-center gap-2 sm:gap-3 rounded-full border border-[#14F195]/40 bg-[#0a0714]/85 hover:border-[#14F195] px-3 sm:px-4 py-1.5 sm:py-2 backdrop-blur-sm transition-colors group max-w-full"
        >
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#14F195]/70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#14F195]" />
          </span>
          <span className="font-bebas tracking-[0.28em] text-[10px] sm:text-xs text-[#9945FF] shrink-0">LATEST</span>
          <span className="font-marker text-sm sm:text-base gradient-sol text-glow-green shrink-0">
            {fmtDropAmount(latestDrop.amount)} $CURB
          </span>
          <span className="text-zinc-500 text-xs sm:text-sm shrink-0">→</span>
          <span
            data-testid="airdrop-latest-recipient"
            className="font-mono text-xs sm:text-sm text-zinc-200 group-hover:text-[#14F195] transition-colors truncate"
          >
            {shortWallet(latestDrop.recipient)}
          </span>
          <span className="text-zinc-600 shrink-0">·</span>
          <span
            data-testid="airdrop-latest-ago"
            className="text-xs sm:text-sm text-zinc-400 shrink-0"
          >
            {latestAgoLabel}
          </span>
          <ExternalLink size={12} className="text-zinc-500 group-hover:text-[#14F195] transition-colors shrink-0 ml-0.5" />
        </a>
      )}

      {/* Stat grid — 2 per row on mobile, 5 across on desktop */}
      <div
        data-testid="airdrop-stats-grid"
        className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4"
      >
        <StatCard
          testId="airdrop-stat-drops"
          kicker="DROPS SENT"
          value={stats.dropsSent.toLocaleString()}
          hint={`across ${state?.txCount || 0} txns`}
          accent="green"
          icon={<Send size={14} className="text-[#14F195]" />}
        />
        <StatCard
          testId="airdrop-stat-wallets"
          kicker="WALLETS FED"
          value={stats.walletsFed.toLocaleString()}
          hint="unique recipients"
          accent="cyan"
          icon={<Users size={14} className="text-[#00FFD1]" />}
        />
        <StatCard
          testId="airdrop-stat-total"
          kicker="TOTAL AIRDROPPED"
          value={`${fmtToken(stats.total)}`}
          hint="$CURB · Ansem excluded"
          accent="mix"
          icon={<Coins size={14} className="text-[#14F195]" />}
        />
        <StatCard
          testId="airdrop-stat-current"
          kicker="CURRENT VALUE"
          value={fmtUsd(stats.currentValue)}
          hint={`live mcap · ${lastUpdatedLabel}`}
          accent="cyan"
          icon={<Radar size={14} className="text-[#00FFD1]" />}
        />
        <StatCard
          testId="airdrop-stat-ath"
          kicker="VALUE AT ATH ($2.76M MCAP)"
          value={fmtUsd(stats.valueAtAth)}
          hint="what the herd would hold if we retag"
          accent="purple"
          icon={<Zap size={14} className="text-[#9945FF]" />}
          className="col-span-2 lg:col-span-1"
        />
      </div>

      <div className="mt-10 sm:mt-14">
        <BubbleMap transfers={transfers.community} newDropTick={newDropTick} />
      </div>

      <p className="text-[10px] text-zinc-600 font-bebas tracking-[0.28em] text-center mt-6">
        SRC · {state?.sourceWallet ? `${state.sourceWallet.slice(0, 4)}…${state.sourceWallet.slice(-4)}` : "……"} · MINT · CURB · {stats.walletsFed} WALLETS · {stats.dropsSent} DROPS
      </p>
    </section>
  );
};

const StatCard = ({ kicker, value, hint, accent, icon, testId, className = "" }) => {
  const border =
    accent === "green"
      ? "border-[#14F195]/40 hover:border-[#14F195]"
      : accent === "purple"
      ? "border-[#9945FF]/40 hover:border-[#9945FF]"
      : accent === "cyan"
      ? "border-[#00FFD1]/40 hover:border-[#00FFD1]"
      : "border-[#9945FF]/40 hover:border-[#9945FF]";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      data-testid={testId}
      className={`relative rounded-2xl border ${border} bg-[#0a0714]/85 backdrop-blur-sm p-4 sm:p-5 transition-colors ${className}`}
    >
      <span
        className="pointer-events-none absolute inset-0 rounded-2xl"
        style={{ boxShadow: "inset 0 0 30px rgba(153,69,255,0.08)" }}
      />
      <p className="font-bebas tracking-[0.28em] text-zinc-400 text-[10px] sm:text-xs flex items-center gap-1.5 leading-tight">
        {icon} {kicker}
      </p>
      <p className="font-marker text-2xl sm:text-3xl lg:text-4xl gradient-sol text-glow-green mt-2 leading-none break-words">
        {value}
      </p>
      <p className="text-zinc-500 text-[10px] sm:text-xs mt-1.5 leading-tight">{hint}</p>
    </motion.div>
  );
};

export default AirdropSummary;
